import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Briefcase, Users, Plus, Trash2, Edit2, UserCheck, UserX, Mail, Calendar, MapPin, X, ChevronLeft, User, Phone, Youtube, ExternalLink, Building2, Gavel, Clock, Bell, CheckCircle, AlertCircle } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  getVendorProjects, createVendorProject, updateVendorProject, deleteVendorProject, cancelVendorProject, reassignEmployee,
  getEmployees, inviteEmployee, updateEmployee, deleteEmployee, resendEmployeeInvite, cancelEmployeeInvite,
  getVendorProject, assignEmployee, unassignEmployee,
  getEmployeeProfile, updateEmployeeProfile,
  getVendorPortal, getAppSettings,
  getProjectHistory, sendProjectReminder, getProjectReminders,
} from '../services/api';
import { toast } from 'react-toastify';

const STATUS_COLORS = {
  upcoming:    { color: '#3b82f6', bg: '#eff6ff' },
  in_progress: { color: '#f59e0b', bg: '#fffbeb' },
  completed:   { color: '#22c55e', bg: '#f0fdf4' },
  cancelled:   { color: '#ef4444', bg: '#fef2f2' },
};

const EMPTY_PACKAGE = { audioRecording: false, droneCoverage: false, fullCeremony: false, highlightsVideo: false, additionalComponents: '' };
const EMPTY_PROJECT = { title: '', description: '', clientName: '', clientPhone: '', clientEmail: '', eventDate: '', location: '', mapUrl: '', status: 'upcoming', notes: '', autoReminderEnabled: false, autoReminderDays: 7, photoDelivered: false, videoDelivered: false, photoDeliveryLink: '', videoDeliveryLink: '', rawPhotoLink: '', rawVideoLink: '', packageDetails: { ...EMPTY_PACKAGE, photoHours: '', videoHours: '' } };
const EMPTY_INVITE  = { email: '', jobTitle: '' };

function VendorProjects() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const STATUS_CONFIG = {
    upcoming:    { label: t('vendorTeam.statusUpcoming'),    ...STATUS_COLORS.upcoming },
    in_progress: { label: t('vendorTeam.statusInProgress'),  ...STATUS_COLORS.in_progress },
    completed:   { label: t('vendorTeam.statusCompleted'),   ...STATUS_COLORS.completed },
    cancelled:   { label: t('vendorTeam.statusCancelled'),   ...STATUS_COLORS.cancelled },
  };
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'team' ? 'team' : 'projects');
  const [vendor, setVendor] = useState({});
  const [biddingEnabled, setBiddingEnabled] = useState(false);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT);
  const [projectSaving, setProjectSaving] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [assigningTo, setAssigningTo] = useState(null);
  // Cancelling a project requires a written reason, so it needs its own modal.
  // Assigning a team member straight from a project card.
  const [reassignTarget, setReassignTarget] = useState(null); // { project, assignment }
  const [reassignToId, setReassignToId] = useState('');
  const [reassignError, setReassignError] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignRoleInput, setAssignRoleInput] = useState('');
  const [assignError, setAssignError] = useState('');
  const [assigningFromCard, setAssigningFromCard] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [assignRole, setAssignRole] = useState('');
  const detailLoadedRef = useRef({});

  // Employee detail (project list per employee)
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeProjects, setEmployeeProjects] = useState([]);
  const [employeeProjectsLoading, setEmployeeProjectsLoading] = useState(false);
  const [editingClientProjectId, setEditingClientProjectId] = useState(null);
  const [editingClientName, setEditingClientName] = useState('');

  // Project list sub-tab (filter by status)
  const [projTab, setProjTab] = useState('all');
  // Team sub-tab (all / photo / video)
  const [teamSubTab, setTeamSubTab] = useState('all');
  // Confirm-before-delete modal
  const [confirmModal, setConfirmModal] = useState(null); // { message, confirmLabel, onConfirm }
  // Reminders
  const [reminders, setReminders] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(false);
  // Assignment history
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  // Edit modal project detail (for inline assignment in modal)
  const [modalProjectDetail, setModalProjectDetail] = useState(null);

  // Employee profile modal
  const [profileModal, setProfileModal] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  // Quick delivery status modal (project list → update delivery without full edit)
  const [deliveryModal, setDeliveryModal] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'vendor') { navigate('/'); return; }
    loadData();
  }, [user, navigate, authLoading]);

  // Auto-refresh employee list every 5s when any invite is pending (so status syncs without manual refresh)
  useEffect(() => {
    const hasPending = employees.some(e => e.invite_status === 'pending' || e.invite_status === 'pending_signup');
    if (!hasPending) return;
    const interval = setInterval(async () => {
      try {
        const res = await getEmployees();
        setEmployees(res.data);
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [employees]);

  async function loadData() {
    setLoading(true);
    try {
      const [pRes, eRes, vRes, sRes] = await Promise.all([getVendorProjects(), getEmployees(), getVendorPortal(), getAppSettings()]);
      setProjects(pRes.data);
      setEmployees(eRes.data);
      setVendor(vRes.data?.vendor || {});
      setBiddingEnabled(sRes.data?.bidding_enabled === 'true');
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function openProjectDetail(project) {
    setSelectedProject(project);
    setReminders([]);
    setAssignmentHistory([]);
    if (!detailLoadedRef.current[project.id]) {
      try {
        const res = await getVendorProject(project.id);
        setProjectDetail(res.data);
        detailLoadedRef.current[project.id] = res.data;
      } catch {
        setProjectDetail({ ...project, assignments: [] });
      }
    } else {
      setProjectDetail(detailLoadedRef.current[project.id]);
    }
    // load side data (don't block detail render)
    Promise.all([
      getProjectReminders(project.id).then(r => setReminders(r.data)).catch(() => {}),
      getProjectHistory(project.id).then(r => setAssignmentHistory(r.data)).catch(() => {}),
    ]);
  }

  async function refreshProjectDetail(projectId) {
    try {
      const res = await getVendorProject(projectId);
      setProjectDetail(res.data);
      detailLoadedRef.current[projectId] = res.data;
    } catch {}
  }

  function openCreateProject() {
    setEditingProject(null);
    setProjectForm(EMPTY_PROJECT);
    setShowProjectModal(true);
  }

  function openEditProject(project) {
    setEditingProject(project);
    const pkg = project.package_details || {};
    setProjectForm({
      title: project.title || '',
      description: project.description || '',
      clientName: project.client_name || '',
      clientPhone: project.client_phone || '',
      clientEmail: project.client_email || '',
      eventDate: project.event_date ? project.event_date.slice(0, 10) : '',
      location: project.location || '',
      mapUrl: project.map_url || '',
      status: project.status || 'upcoming',
      notes: project.notes || '',
      autoReminderEnabled: project.auto_reminder_enabled || false,
      autoReminderDays: project.auto_reminder_days || 7,
      photoDelivered: project.photo_delivered || false,
      videoDelivered: project.video_delivered || false,
      photoDeliveryLink: project.photo_delivery_link || '',
      videoDeliveryLink: project.video_delivery_link || '',
      rawPhotoLink: project.raw_photo_link || '',
      rawVideoLink: project.raw_video_link || '',
      packageDetails: {
        audioRecording: pkg.audioRecording || false,
        droneCoverage: pkg.droneCoverage || false,
        fullCeremony: pkg.fullCeremony || false,
        highlightsVideo: pkg.highlightsVideo || false,
        additionalComponents: pkg.additionalComponents || '',
        photoHours: pkg.photoHours || pkg.hoursCoverage || '',
        videoHours: pkg.videoHours || '',
      },
    });
    setModalProjectDetail(null);
    // Load detail for assignment section in modal
    if (detailLoadedRef.current[project.id]) {
      setModalProjectDetail(detailLoadedRef.current[project.id]);
    } else {
      getVendorProject(project.id).then(res => {
        setModalProjectDetail(res.data);
        detailLoadedRef.current[project.id] = res.data;
      }).catch(() => {});
    }
    setAssigningTo(null);
    setAssignRole('');
    setShowProjectModal(true);
  }

  async function saveProject() {
    if (!projectForm.title.trim()) { toast.error('Project title is required'); return; }
    if (projectForm.photoDelivered && !projectForm.photoDeliveryLink?.trim()) {
      toast.error('Photo delivery link is required when photos are marked delivered'); return;
    }
    if (projectForm.photoDelivered && !projectForm.packageDetails?.photoHours) {
      toast.error('Photo hours of coverage is required when photos are marked delivered'); return;
    }
    if (projectForm.videoDelivered && !projectForm.videoDeliveryLink?.trim()) {
      toast.error('Video delivery link is required when videos are marked delivered'); return;
    }
    if (projectForm.videoDelivered && !projectForm.packageDetails?.videoHours) {
      toast.error('Video hours of coverage is required when videos are marked delivered'); return;
    }
    setProjectSaving(true);
    try {
      if (editingProject) {
        const res = await updateVendorProject(editingProject.id, projectForm);
        const merged = { ...(detailLoadedRef.current[editingProject.id] || {}), ...res.data };
        detailLoadedRef.current[editingProject.id] = merged;
        setModalProjectDetail(merged);
        if (selectedProject?.id === editingProject.id) setProjectDetail(merged);
        toast.success('Project updated');
      } else {
        await createVendorProject(projectForm);
        detailLoadedRef.current = {};
        toast.success('Project created');
      }
      setShowProjectModal(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save project');
    } finally {
      setProjectSaving(false);
    }
  }

  function openReassignModal(project, assignment) {
    setReassignTarget({ project, assignment });
    setReassignToId('');
    setReassignError('');
  }

  // Swaps one member for another in a single call: the outgoing member is told
  // they are off the project, the incoming one is briefed, and the client gets
  // the updated roster.
  async function handleReassign() {
    if (!reassignToId) {
      setReassignError('Pick who should take this over.');
      return;
    }
    setReassigning(true);
    setReassignError('');
    try {
      const res = await reassignEmployee(reassignTarget.project.id, {
        fromEmployeeId: reassignTarget.assignment.employee_id,
        toEmployeeId: Number(reassignToId),
      });
      toast.success(`Reassigned to ${res.data.to} — both were emailed`);
      setReassignTarget(null);
      const [detail, list] = await Promise.all([
        getVendorProject(reassignTarget.project.id).catch(() => null),
        getVendorProjects().catch(() => null),
      ]);
      if (detail) setProjectDetail(detail.data);
      if (list) setProjects(list.data);
    } catch (err) {
      setReassignError(err.response?.data?.error || 'Failed to reassign');
    } finally {
      setReassigning(false);
    }
  }

  function openAssignModal(project) {
    setAssignTarget(project);
    setAssignEmployeeId('');
    setAssignRoleInput('');
    setAssignError('');
  }

  // Assigning from the project list. Sends the team-member notification and the
  // client's "your team has been assigned" email via the assign endpoint.
  async function handleAssignFromCard() {
    if (!assignEmployeeId) {
      setAssignError('Pick a team member to assign.');
      return;
    }
    setAssigningFromCard(true);
    setAssignError('');
    try {
      await assignEmployee(assignTarget.id, {
        employeeId: Number(assignEmployeeId),
        roleOnProject: assignRoleInput.trim() || null,
      });
      toast.success('Team member assigned — notification emails sent');
      setAssignTarget(null);
      const res = await getVendorProjects();
      setProjects(res.data);
    } catch (err) {
      setAssignError(err.response?.data?.error || 'Failed to assign team member');
    } finally {
      setAssigningFromCard(false);
    }
  }

  async function handleCancelProject() {
    const reason = cancelReason.trim();
    if (reason.length < 10) {
      setCancelError('Please give a reason of at least 10 characters.');
      return;
    }
    setCancelling(true);
    setCancelError('');
    try {
      await cancelVendorProject(cancelTarget.id, reason);
      toast.success('Project cancelled');
      setCancelTarget(null);
      setCancelReason('');
      const res = await getVendorProjects();
      setProjects(res.data);
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Failed to cancel project');
    } finally {
      setCancelling(false);
    }
  }

  async function handleDeleteProject(id) {
    setConfirmModal({
      message: 'Delete this project? All assignments and history will be permanently removed.',
      confirmLabel: 'Delete Project',
      onConfirm: async () => {
        try {
          await deleteVendorProject(id);
          toast.success('Project deleted');
          if (selectedProject?.id === id) { setSelectedProject(null); setProjectDetail(null); }
          await loadData();
        } catch { toast.error('Failed to delete project'); }
      },
    });
  }

  function openDeliveryModal(project, e) {
    e.stopPropagation();
    const pkg = project.package_details || {};
    setDeliveryModal({
      project,
      photoDelivered: project.photo_delivered || false,
      videoDelivered: project.video_delivered || false,
      photoDeliveryLink: project.photo_delivery_link || '',
      videoDeliveryLink: project.video_delivery_link || '',
      rawPhotoLink: project.raw_photo_link || '',
      rawVideoLink: project.raw_video_link || '',
      photoHours: pkg.photoHours || pkg.hoursCoverage || '',
      videoHours: pkg.videoHours || '',
      saving: false,
    });
  }

  async function saveDelivery() {
    const dm = deliveryModal;
    if (dm.photoDelivered && !dm.photoDeliveryLink?.trim()) { toast.error('Photo delivery link is required'); return; }
    if (dm.videoDelivered && !dm.videoDeliveryLink?.trim()) { toast.error('Video delivery link is required'); return; }
    setDeliveryModal(d => ({ ...d, saving: true }));
    try {
      const pkg = dm.project.package_details || {};
      const res = await updateVendorProject(dm.project.id, {
        photoDelivered: dm.photoDelivered,
        videoDelivered: dm.videoDelivered,
        photoDeliveryLink: dm.photoDeliveryLink,
        videoDeliveryLink: dm.videoDeliveryLink,
        rawPhotoLink: dm.rawPhotoLink,
        rawVideoLink: dm.rawVideoLink,
        packageDetails: { ...pkg, photoHours: dm.photoHours, videoHours: dm.videoHours },
      });
      const merged = { ...(detailLoadedRef.current[dm.project.id] || {}), ...res.data };
      detailLoadedRef.current[dm.project.id] = merged;
      if (selectedProject?.id === dm.project.id) setProjectDetail(merged);
      toast.success('Delivery status updated');
      setDeliveryModal(null);
      const pRes = await getVendorProjects();
      setProjects(pRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update delivery');
      setDeliveryModal(d => ({ ...d, saving: false }));
    }
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!inviteForm.email.trim()) { toast.error('Email is required'); return; }
    setInviteSaving(true);
    try {
      await inviteEmployee(inviteForm);
      toast.success(`Invite sent to ${inviteForm.email}`);
      setInviteForm(EMPTY_INVITE);
      setShowInviteForm(false);
      const res = await getEmployees();
      setEmployees(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invite');
    } finally {
      setInviteSaving(false);
    }
  }

  async function handleReinstateEmployee(id) {
    setConfirmModal({
      message: "Reinstate this employee? They will receive a new invitation email.",
      confirmLabel: 'Reinstate',
      onConfirm: async () => {
        try {
          await updateEmployee(id, { inviteStatus: 'pending' });
          await resendEmployeeInvite(id);
          toast.success('Employee reinstated and invitation sent');
          const res = await getEmployees();
          setEmployees(res.data);
        } catch { toast.error('Failed to reinstate employee'); }
      },
    });
  }

  async function handleRevokeEmployee(id) {
    setConfirmModal({
      message: "Revoke this employee's access? They will no longer be able to log in as part of your team.",
      confirmLabel: 'Revoke Access',
      onConfirm: async () => {
        try {
          await updateEmployee(id, { inviteStatus: 'revoked' });
          toast.success('Employee revoked');
          const res = await getEmployees();
          setEmployees(res.data);
        } catch { toast.error('Failed to revoke employee'); }
      },
    });
  }

  async function handleDeleteEmployee(id) {
    // Removing the record cascades their project assignments away, so say so.
    const onProjects = projects.filter(p => (p.assignees || []).some(a => a.id === id)).length;
    setConfirmModal({
      message: onProjects > 0
        ? `Remove this team member permanently? They will also be taken off ${onProjects} project${onProjects === 1 ? '' : 's'} they are currently assigned to. This cannot be undone.`
        : 'Remove this employee record permanently? This cannot be undone.',
      confirmLabel: 'Remove',
      onConfirm: async () => {
        try {
          await deleteEmployee(id);
          toast.success('Employee removed');
          const res = await getEmployees();
          setEmployees(res.data);
        } catch { toast.error('Failed to remove employee'); }
      },
    });
  }

  async function handleCancelInvite(emp) {
    setConfirmModal({
      message: `Cancel the invitation sent to ${emp.invited_email}? Their invite link will stop working immediately. You can send a new one later.`,
      confirmLabel: 'Cancel Invitation',
      onConfirm: async () => {
        try {
          await cancelEmployeeInvite(emp.id);
          toast.success('Invitation cancelled — the link is no longer valid');
          const res = await getEmployees();
          setEmployees(res.data);
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to cancel invitation');
        }
      },
    });
  }

  async function handleAssign() {
    if (!assigningTo) return;
    try {
      await assignEmployee(projectDetail.id, { employeeId: assigningTo, roleOnProject: assignRole });
      toast.success('Employee assigned');
      setAssigningTo(null);
      setAssignRole('');
      await refreshProjectDetail(projectDetail.id);
      getProjectHistory(projectDetail.id).then(r => setAssignmentHistory(r.data)).catch(() => {});
      const pRes = await getVendorProjects();
      setProjects(pRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign');
    }
  }

  async function handleUnassign(employeeId) {
    try {
      await unassignEmployee(projectDetail.id, employeeId);
      toast.success('Employee unassigned');
      await refreshProjectDetail(projectDetail.id);
      getProjectHistory(projectDetail.id).then(r => setAssignmentHistory(r.data)).catch(() => {});
    } catch {
      toast.error('Failed to unassign');
    }
  }

  async function openEmployeeProfile(emp) {
    setProfileModal(emp);
    setProfileData(null);
    setProfileLoading(true);
    try {
      const res = await getEmployeeProfile(emp.id);
      const d = res.data;
      setProfileData(d);
      setProfileForm({
        firstName:        d.first_name        || '',
        lastName:         d.last_name         || '',
        phone:            d.phone             || '',
        jobTitle:         d.job_title         || '',
        dateOfBirth:      d.date_of_birth     ? (d.date_of_birth + '').slice(0, 10) : '',
        address:          d.address           || '',
        youtubeUrl:       d.youtube_url       || '',
        bio:              d.bio               || '',
        emergencyContact: d.emergency_contact || '',
        employeeType:     d.employee_type     || '',
        location:         d.location          || '',
        portfolioUrl:     d.portfolio_url     || '',
      });
    } catch {
      toast.error('Failed to load profile');
      setProfileModal(null);
    } finally {
      setProfileLoading(false);
    }
  }

  async function saveEmployeeProfile() {
    if (!profileModal) return;
    setProfileSaving(true);
    try {
      const res = await updateEmployeeProfile(profileModal.id, profileForm);
      toast.success('Profile saved');
      setProfileModal(null);
      setProfileData(res.data);
      const eRes = await getEmployees();
      setEmployees(eRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD';

  // Days remaining until event
  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const datePart = String(dateStr).split('T')[0];
    const event = new Date(datePart + 'T00:00:00');
    const diff = Math.ceil((event - today) / 86400000);
    if (diff < 0) return `${Math.abs(diff)}d ago`;
    if (diff === 0) return 'Today!';
    if (diff === 1) return 'Tomorrow';
    return `${diff}d away`;
  };

  // Build a Google Maps URL from location text or an explicit map_url
  const mapsUrl = (location, mapUrl) => {
    if (mapUrl) return mapUrl;
    if (!location) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
  };

  async function handleSendReminder() {
    setSendingReminder(true);
    try {
      await sendProjectReminder(projectDetail.id);
      toast.success('Reminder emails sent to all assigned team members!');
      const rRes = await getProjectReminders(projectDetail.id);
      setReminders(rRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  }

  const openEmployeeDetail = async (emp) => {
    setSelectedEmployee(emp);
    setEmployeeProjectsLoading(true);
    setEditingClientProjectId(null);
    try {
      const toLoad = projects.filter(p => !detailLoadedRef.current[p.id]);
      await Promise.all(toLoad.map(async p => {
        try { const res = await getVendorProject(p.id); detailLoadedRef.current[p.id] = res.data; } catch {}
      }));
      const empProjects = projects.filter(p => {
        const d = detailLoadedRef.current[p.id];
        return d?.assignments?.some(a => a.employee_id === emp.id);
      });
      setEmployeeProjects(empProjects);
    } finally {
      setEmployeeProjectsLoading(false);
    }
  };

  const handleUpdateProjectClient = async (projectId) => {
    try {
      await updateVendorProject(projectId, { clientName: editingClientName });
      if (detailLoadedRef.current[projectId]) {
        detailLoadedRef.current[projectId] = { ...detailLoadedRef.current[projectId], client_name: editingClientName };
      }
      setProjects(ps => ps.map(p => p.id === projectId ? { ...p, client_name: editingClientName } : p));
      setEmployeeProjects(eps => eps.map(p => p.id === projectId ? { ...p, client_name: editingClientName } : p));
      setEditingClientProjectId(null);
      toast.success('Client updated');
    } catch {
      toast.error('Failed to update client');
    }
  };
  const acceptedEmployees = employees.filter(e => e.invite_status === 'accepted');
  // Assignable = everyone still on the team. Someone who has not accepted yet is
  // still a real hire, gets the notification at their invited address, and shows
  // to the client as "Confirming" until they accept.
  const assignableEmployees = employees.filter(e => e.invite_status !== 'revoked');
  const assignedIds = new Set((projectDetail?.assignments || []).map(a => a.employee_id));
  const unassignedEmployees = acceptedEmployees.filter(e => !assignedIds.has(e.id));

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#374151' };
  const isLocked = projectForm.status === 'completed';

  if (loading) {
    return (
      <div className="vendor-portal">
        <div className="portal-container" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div className="spinner"></div>
          <p style={{ color: '#6b7280', marginTop: '12px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-portal">
      <div className="portal-container">

        {/* Portal Header — same gradient hero as VendorPortal */}
        <div className="portal-header">
          <div className="portal-header-info">
            <h1>{vendor.business_name || 'Your Business'}</h1>
            <p className="portal-category">{vendor.category_name} &middot; {vendor.city}</p>
          </div>
          <div className="portal-header-stats">
            <div className="portal-stat">
              <Briefcase size={18} />
              <span className="portal-stat-value">{projects.length}</span>
              <span className="portal-stat-label">Projects</span>
            </div>
            <div className="portal-stat">
              <Users size={18} />
              <span className="portal-stat-value">{acceptedEmployees.length}</span>
              <span className="portal-stat-label">Team</span>
            </div>
            <div className="portal-stat">
              <Calendar size={18} />
              <span className="portal-stat-value">{projects.filter(p => p.status === 'upcoming').length}</span>
              <span className="portal-stat-label">Upcoming</span>
            </div>
          </div>
        </div>

        {/* Approval banner — above tabs */}
        {vendor.approval_status === 'approved' && (
          <div className="approval-banner approved">
            <CheckCircle size={20} />
            <div>
              <strong>Profile Approved</strong>
              <p>Your vendor profile is live! Couples can now find and contact you.</p>
            </div>
          </div>
        )}
        {vendor.approval_status === 'pending' && (
          <div className="approval-banner pending">
            <Clock size={20} />
            <div>
              <strong>Approval Pending</strong>
              <p>Your profile is under review. Team &amp; project features are available while you wait.</p>
            </div>
          </div>
        )}

        {/* Unified tab strip */}
        <div className="portal-tabs">
          <button className="portal-tab" onClick={() => navigate('/vendor-portal?tab=overview')}>Overview</button>
          <button className="portal-tab" onClick={() => navigate('/vendor-portal?tab=inquiries')}>Inquiries</button>
          <button className="portal-tab" onClick={() => navigate('/vendor-portal?tab=nepwed-reviews')}>NepWed Reviews</button>
          <a
            href={vendor.google_place_id
              ? `https://search.google.com/local/reviews?placeid=${vendor.google_place_id}`
              : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((vendor.business_name || '') + ' ' + (vendor.city || ''))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="portal-tab"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: 14, height: 14 }} />
            Google Reviews <ExternalLink size={12} />
          </a>
          <button className="portal-tab" onClick={() => navigate('/vendor-portal?tab=photos')}>Photos &amp; Videos</button>
          {vendor.is_verified && biddingEnabled && (
            <button className="portal-tab" onClick={() => navigate('/vendor-portal?tab=bidding')}>
              <Gavel size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />Bidding
            </button>
          )}
          <button className={`portal-tab ${activeTab === 'team' ? 'active' : ''}`} onClick={() => setActiveTab('team')}>
            <Users size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{t('vendorTeam.teamTab')}
          </button>
          <button className={`portal-tab ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => setActiveTab('projects')}>
            <Briefcase size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{t('vendorTeam.projectsTab')}
          </button>
        </div>

        {/* ===== PROJECTS TAB ===== */}
        {activeTab === 'projects' && (
          <div>
            {selectedProject ? (
              /* Project Detail View */
              <div>
                <button className="btn btn-outline" onClick={() => { setSelectedProject(null); setProjectDetail(null); }}
                  style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ChevronLeft size={16} /> {t('vendorTeam.backToProjects')}
                </button>
                {projectDetail ? (
                  <div className="portal-info-card" style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                          <h2 style={{ margin: 0, fontSize: '20px' }}>{projectDetail.title}</h2>
                          <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '20px',
                            background: STATUS_CONFIG[projectDetail.status]?.bg, color: STATUS_CONFIG[projectDetail.status]?.color }}>
                            {STATUS_CONFIG[projectDetail.status]?.label}
                          </span>
                          {(() => { const d = daysUntil(projectDetail.event_date); return d && projectDetail.status !== 'completed' && projectDetail.status !== 'cancelled' ? (
                            <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                              background: d.includes('ago') ? '#fef2f2' : d === 'Today!' ? '#fef9c3' : '#eff6ff',
                              color: d.includes('ago') ? '#ef4444' : d === 'Today!' ? '#ca8a04' : '#3b82f6' }}>
                              <Clock size={12} /> {d}
                            </span>
                          ) : null; })()}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', color: '#6b7280', fontSize: '13px' }}>
                          {projectDetail.client_name && <span>Client: <strong>{projectDetail.client_name}</strong></span>}
                          {projectDetail.client_phone && (
                            <a href={`tel:${projectDetail.client_phone}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}
                              onMouseOver={e => e.currentTarget.style.color='#be185d'} onMouseOut={e => e.currentTarget.style.color='#6b7280'}>
                              <Phone size={13} />{projectDetail.client_phone}
                            </a>
                          )}
                          {projectDetail.client_email && (
                            <a href={`mailto:${projectDetail.client_email}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}
                              onMouseOver={e => e.currentTarget.style.color='#be185d'} onMouseOut={e => e.currentTarget.style.color='#6b7280'}>
                              <Mail size={13} />{projectDetail.client_email}
                            </a>
                          )}
                          {projectDetail.event_date && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Calendar size={13} />{formatDate(projectDetail.event_date)}</span>}
                          {projectDetail.location && (() => {
                            const url = mapsUrl(projectDetail.location, projectDetail.map_url);
                            return (
                              <a href={url} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}
                                onMouseOver={e => e.currentTarget.style.color='#be185d'} onMouseOut={e => e.currentTarget.style.color='#6b7280'}>
                                <MapPin size={13} />{projectDetail.location} <ExternalLink size={11} />
                              </a>
                            );
                          })()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-outline" onClick={() => openEditProject(projectDetail)}
                          style={{ padding: '7px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Edit2 size={14} /> Edit
                        </button>
                        <button className="btn btn-outline" onClick={() => handleDeleteProject(projectDetail.id)}
                          style={{ padding: '7px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', color: '#ef4444', borderColor: '#fca5a5' }}>
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </div>

                    {projectDetail.description && (
                      <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>{projectDetail.description}</p>
                    )}
                    {projectDetail.notes && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#92400e' }}>
                        <strong>Notes:</strong> {projectDetail.notes}
                      </div>
                    )}

                    {/* Package Details */}
                    {projectDetail.package_details && Object.values(projectDetail.package_details).some(v => v !== '' && v !== false && v !== null) && (
                      <div style={{ background: '#f8f9ff', border: '1px solid #e0e7ff', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#4338ca', marginBottom: '10px' }}>Package Inclusions</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                          {(projectDetail.package_details.photoHours || projectDetail.package_details.hoursCoverage) && (
                            <span style={{ fontSize: '13px', color: '#374151' }}>📷 {projectDetail.package_details.photoHours || projectDetail.package_details.hoursCoverage}h photo coverage</span>
                          )}
                          {projectDetail.package_details.videoHours && (
                            <span style={{ fontSize: '13px', color: '#374151' }}>🎥 {projectDetail.package_details.videoHours}h video coverage</span>
                          )}
                          {projectDetail.package_details.audioRecording && (
                            <span style={{ fontSize: '13px', color: '#374151' }}>🎙 Audio recording</span>
                          )}
                          {projectDetail.package_details.droneCoverage && (
                            <span style={{ fontSize: '13px', color: '#374151' }}>🚁 Drone coverage</span>
                          )}
                          {projectDetail.package_details.fullCeremony && (
                            <span style={{ fontSize: '13px', color: '#374151' }}>📽 Full ceremony video</span>
                          )}
                          {projectDetail.package_details.highlightsVideo && (
                            <span style={{ fontSize: '13px', color: '#374151' }}>✨ Highlights video</span>
                          )}
                          {projectDetail.package_details.additionalComponents && (
                            <span style={{ fontSize: '13px', color: '#374151', gridColumn: '1/-1' }}>📋 {projectDetail.package_details.additionalComponents}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Delivery Status */}
                    {(projectDetail.photo_delivered || projectDetail.video_delivered || projectDetail.photo_delivery_link || projectDetail.video_delivery_link) && (
                      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#15803d', marginBottom: '10px' }}>Delivery Status</div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#374151' }}>
                              {projectDetail.photo_delivered
                                ? <><CheckCircle size={15} color="#22c55e" /> Photos Delivered</>
                                : <><AlertCircle size={15} color="#9ca3af" /> Photos Pending</>}
                            </span>
                            {projectDetail.photo_delivered && projectDetail.photo_delivery_link && (
                              <a href={projectDetail.photo_delivery_link} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '12px', color: '#be185d', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <ExternalLink size={11} /> View Photos
                              </a>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#374151' }}>
                              {projectDetail.video_delivered
                                ? <><CheckCircle size={15} color="#22c55e" /> Videos Delivered</>
                                : <><AlertCircle size={15} color="#9ca3af" /> Videos Pending</>}
                            </span>
                            {projectDetail.video_delivered && projectDetail.video_delivery_link && (
                              <a href={projectDetail.video_delivery_link} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '12px', color: '#be185d', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <ExternalLink size={11} /> View Videos
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Assignments */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <h3 style={{ margin: 0, fontSize: '15px' }}>{t('vendorTeam.assignedTeam')}</h3>
                        {unassignedEmployees.length > 0 && (
                          <button className="btn btn-primary" onClick={() => setAssigningTo('open')}
                            style={{ padding: '7px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Plus size={14} /> Assign Member
                          </button>
                        )}
                      </div>

                      {assigningTo !== null && (
                        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                          <label style={labelStyle}>Select Employee</label>
                          <select style={{ ...inputStyle, marginBottom: '10px' }} value={assigningTo === 'open' ? '' : assigningTo}
                            onChange={e => setAssigningTo(Number(e.target.value))}>
                            <option value="">-- Select --</option>
                            {unassignedEmployees.map(e => (
                              <option key={e.id} value={e.id}>
                                {e.first_name ? `${e.first_name} ${e.last_name || ''}` : e.invited_email}{e.job_title ? ` (${e.job_title})` : ''}
                              </option>
                            ))}
                          </select>
                          <label style={labelStyle}>Role on this project (optional)</label>
                          <input style={{ ...inputStyle, marginBottom: '12px' }} placeholder="e.g. Lead Photographer" value={assignRole}
                            onChange={e => setAssignRole(e.target.value)} />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="btn btn-primary" onClick={handleAssign} disabled={!assigningTo || assigningTo === 'open'}
                              style={{ padding: '8px 18px', fontSize: '13px' }}>Assign</button>
                            <button className="btn btn-outline" onClick={() => { setAssigningTo(null); setAssignRole(''); }}
                              style={{ padding: '8px 18px', fontSize: '13px' }}>Cancel</button>
                          </div>
                        </div>
                      )}

                      {(projectDetail.assignments || []).length === 0 ? (
                        <p style={{ color: '#9ca3af', fontSize: '13px' }}>{t('vendorTeam.noMembersAssigned')}</p>
                      ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                          {projectDetail.assignments.map(a => (
                            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              background: '#f9fafb', borderRadius: '8px', padding: '12px 16px' }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '14px' }}>
                                  {a.first_name ? `${a.first_name} ${a.last_name || ''}` : a.invited_email}
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                                  {a.role_on_project || a.job_title || 'Team member'}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <button className="btn btn-outline" title="Swap for another team member"
                                onClick={() => openReassignModal(projectDetail, a)}
                                style={{ padding: '4px 10px', fontSize: '12px', color: '#1d4ed8', borderColor: '#93c5fd' }}>
                                Reassign
                              </button>
                              <button onClick={() => handleUnassign(a.employee_id)} title="Remove from project"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}>
                                <X size={16} />
                              </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ── Reminder Panel ── */}
                    <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '20px', marginTop: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <h3 style={{ margin: 0, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Bell size={16} color="#be185d" /> Event Reminders
                        </h3>
                        <button className="btn btn-primary" onClick={handleSendReminder}
                          disabled={sendingReminder || projectDetail.status === 'completed' || (projectDetail.assignments || []).length === 0}
                          style={{ padding: '7px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Bell size={13} /> {sendingReminder ? 'Sending…' : 'Send Reminder'}
                        </button>
                      </div>
                      {reminders.length === 0 ? (
                        <p style={{ color: '#9ca3af', fontSize: '13px' }}>No reminders sent yet.</p>
                      ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                          {reminders.map(r => {
                            const total = (r.recipients || []).filter(x => x.employee_email).length;
                            const acked = (r.recipients || []).filter(x => x.acknowledged_at).length;
                            return (
                              <div key={r.id} style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px 16px', border: '1px solid #f3f4f6' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                                    Sent {new Date(r.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 10px', borderRadius: '20px',
                                    background: acked === total ? '#f0fdf4' : '#fffbeb',
                                    color: acked === total ? '#22c55e' : '#f59e0b' }}>
                                    {acked}/{total} acknowledged
                                  </span>
                                </div>
                                <div style={{ display: 'grid', gap: '4px' }}>
                                  {(r.recipients || []).filter(x => x.employee_email).map(x => (
                                    <div key={x.employee_email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                                      <span style={{ color: '#6b7280' }}>{x.employee_email}</span>
                                      {x.acknowledged_at ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#22c55e', fontWeight: 600 }}>
                                          <CheckCircle size={12} /> Acknowledged
                                        </span>
                                      ) : (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#f59e0b' }}>
                                          <AlertCircle size={12} /> Pending
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* ── Assignment History ── */}
                    {assignmentHistory.length > 0 && (
                      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '20px', marginTop: '20px' }}>
                        <h3 style={{ margin: '0 0 14px', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={16} color="#9ca3af" /> Assignment History
                        </h3>
                        <div style={{ display: 'grid', gap: '6px' }}>
                          {assignmentHistory.map(h => (
                            <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#6b7280', padding: '8px 12px', background: '#f9fafb', borderRadius: '8px', gap: '12px' }}>
                              <span>
                                <strong style={{ color: '#374151' }}>{h.employee_email}</strong>
                                {' '}<span style={{ color: h.action === 'assigned' ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{h.action}</span>
                                {h.role_on_project && <span style={{ color: '#9ca3af' }}> · {h.role_on_project}</span>}
                              </span>
                              <span style={{ whiteSpace: 'nowrap' }}>
                                {h.changed_by_name?.trim() && <span style={{ marginRight: '6px' }}>by {h.changed_by_name.trim()}</span>}
                                {new Date(h.changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="spinner"></div>
                  </div>
                )}
              </div>
            ) : (
              /* Project List View */
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  {/* Status sub-tabs */}
                  <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '10px', padding: '3px' }}>
                    {[
                      ['all', 'All', projects.length],
                      ['upcoming', 'Upcoming', projects.filter(p => p.status === 'upcoming').length],
                      ['in_progress', 'In Progress', projects.filter(p => p.status === 'in_progress').length],
                      ['completed', 'Completed', projects.filter(p => p.status === 'completed').length],
                    ].map(([val, label, count]) => (
                      <button key={val} onClick={() => setProjTab(val)}
                        style={{ padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                          background: projTab === val ? '#fff' : 'transparent',
                          color: projTab === val ? '#be185d' : '#6b7280',
                          boxShadow: projTab === val ? '0 1px 3px rgba(0,0,0,.1)' : 'none' }}>
                        {label} <span style={{ opacity: 0.7 }}>({count})</span>
                      </button>
                    ))}
                  </div>
                  <button className="btn btn-primary" onClick={openCreateProject}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Plus size={16} /> {t('vendorTeam.newProject')}
                  </button>
                </div>

                {(() => {
                  const filtered = projTab === 'all' ? projects : projects.filter(p => p.status === projTab);
                  if (filtered.length === 0) return (
                    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: '12px' }}>
                      <Briefcase size={48} color="#d1d5db" />
                      <h3 style={{ color: '#6b7280', marginTop: '16px' }}>
                        {projects.length === 0 ? t('vendorTeam.noProjectsYet') : `No ${projTab === 'all' ? '' : STATUS_CONFIG[projTab]?.label} projects`}
                      </h3>
                      <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                        {projects.length === 0 ? t('vendorTeam.noProjectsText') : t('common.tryDifferentFilter')}
                      </p>
                      {projects.length === 0 && (
                        <button className="btn btn-primary" onClick={openCreateProject} style={{ marginTop: '12px' }}>
                          <Plus size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Create Project
                        </button>
                      )}
                    </div>
                  );
                  return (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {filtered.map(project => {
                        const cfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.upcoming;
                        const days = daysUntil(project.event_date);
                        const locUrl = mapsUrl(project.location, project.map_url);
                        return (
                          <div key={project.id} className="portal-info-card"
                            style={{ padding: '18px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                            onClick={() => openProjectDetail(project)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                  <strong style={{ fontSize: '15px' }}>{project.title}</strong>
                                  <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                                  {days && project.status !== 'completed' && project.status !== 'cancelled' && (
                                    <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                      background: days.includes('ago') ? '#fef2f2' : days === 'Today!' ? '#fef9c3' : '#eff6ff',
                                      color: days.includes('ago') ? '#ef4444' : days === 'Today!' ? '#ca8a04' : '#3b82f6',
                                      display: 'flex', alignItems: 'center', gap: '3px' }}>
                                      <Clock size={10} /> {days}
                                    </span>
                                  )}
                                </div>
                                {project.status === 'cancelled' && project.cancellation_reason && (
                                  <div className="project-cancel-note">
                                    <AlertCircle size={12} />
                                    <span><strong>Cancelled:</strong> {project.cancellation_reason}</span>
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', color: '#6b7280', fontSize: '12px' }}>
                                  {project.client_name && <span>{project.client_name}</span>}
                                  {project.event_date && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><Calendar size={12} />{formatDate(project.event_date)}</span>}
                                  {project.location && (
                                    <a href={locUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                      style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#6b7280', textDecoration: 'none' }}
                                      onMouseOver={e => e.currentTarget.style.color='#be185d'} onMouseOut={e => e.currentTarget.style.color='#6b7280'}>
                                      <MapPin size={12} />{project.location}
                                    </a>
                                  )}
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                    <Users size={12} />{project.assignment_count || 0} assigned
                                    {(project.assignees || []).length > 0 && (
                                      <span style={{ marginLeft: '2px' }}>
                                        ({project.assignees.map(a => a.role ? `${a.name} (${a.role})` : a.name).join(', ')})
                                      </span>
                                    )}
                                  </span>
                                  {project.package_details?.audioRecording && <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>🎙 Audio</span>}
                                  {project.package_details?.droneCoverage && <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>🚁 Drone</span>}
                                  {project.package_details?.fullCeremony && <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>📽 Full Ceremony</span>}
                                  {project.package_details?.highlightsVideo && <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>✨ Highlights</span>}
                                  {project.package_details?.additionalComponents && <span>📋 {project.package_details.additionalComponents}</span>}
                                </div>
                                {/* Delivery status row */}
                                {(project.package_details?.photoHours || project.package_details?.videoHours || project.photo_delivered || project.video_delivered) && (
                                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '5px' }}>
                                    {(project.package_details?.photoHours || project.photo_delivered !== undefined) && (
                                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                        background: project.photo_delivered ? '#f0fdf4' : '#f9fafb',
                                        color: project.photo_delivered ? '#16a34a' : '#9ca3af',
                                        display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        📷{project.package_details?.photoHours ? ` ${project.package_details.photoHours}h ·` : ''} {project.photo_delivered ? 'Delivered' : 'Pending'}
                                      </span>
                                    )}
                                    {(project.package_details?.videoHours || project.video_delivered !== undefined) && (
                                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                        background: project.video_delivered ? '#f0fdf4' : '#f9fafb',
                                        color: project.video_delivered ? '#16a34a' : '#9ca3af',
                                        display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        🎥{project.package_details?.videoHours ? ` ${project.package_details.videoHours}h ·` : ''} {project.video_delivered ? 'Delivered' : 'Pending'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                                <button className="btn btn-outline" onClick={() => openEditProject(project)}
                                  style={{ padding: '5px 10px', fontSize: '12px' }}><Edit2 size={12} /></button>
                                {project.status !== 'cancelled' && project.status !== 'completed' && (
                                  <button className="btn btn-outline" title="Assign team member"
                                    onClick={() => openAssignModal(project)}
                                    style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: '#1d4ed8', borderColor: '#93c5fd' }}>
                                    <UserCheck size={12} /> Assign
                                  </button>
                                )}
                                {project.status !== 'cancelled' && project.status !== 'completed' && (
                                  <button className="btn btn-outline" title="Cancel project"
                                    onClick={() => setCancelTarget(project)}
                                    style={{ padding: '5px 10px', fontSize: '12px', color: '#b45309', borderColor: '#fcd34d' }}>
                                    <X size={12} />
                                  </button>
                                )}
                                <button className="btn btn-outline" onClick={() => handleDeleteProject(project.id)}
                                  style={{ padding: '5px 10px', fontSize: '12px', color: '#ef4444', borderColor: '#fca5a5' }}><Trash2 size={12} /></button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ===== TEAM TAB ===== */}
        {activeTab === 'team' && (
          <div>
            {selectedEmployee ? (
              /* ── Employee Detail View ── */
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <button className="btn btn-outline" onClick={() => { setSelectedEmployee(null); setEmployeeProjects([]); setEditingClientProjectId(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '13px' }}>
                    <ChevronLeft size={15} /> Back to Team
                  </button>
                  <button className="btn btn-outline" onClick={() => openEmployeeProfile(selectedEmployee)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '13px' }}>
                    <User size={13} /> Edit Full Profile
                  </button>
                </div>

                {/* Employee info card */}
                <div className="portal-info-card" style={{ marginBottom: '20px', padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg,#fce7f3,#fbcfe8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <User size={22} color="#be185d" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '17px', marginBottom: '3px' }}>
                        {selectedEmployee.first_name ? `${selectedEmployee.first_name} ${selectedEmployee.last_name || ''}` : selectedEmployee.invited_email}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '13px', color: '#6b7280', alignItems: 'center' }}>
                        {selectedEmployee.job_title && <span>{selectedEmployee.job_title}</span>}
                        {selectedEmployee.first_name && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><Mail size={12} />{selectedEmployee.invited_email}</span>}
                        <span style={{
                          fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                          background: selectedEmployee.invite_status === 'accepted' ? '#f0fdf4' : '#fffbeb',
                          color: selectedEmployee.invite_status === 'accepted' ? '#22c55e' : '#f59e0b',
                        }}>
                          {selectedEmployee.invite_status === 'accepted' ? 'Active' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Their projects */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>
                    Assigned Projects {!employeeProjectsLoading && `(${employeeProjects.length})`}
                  </h3>
                </div>

                {employeeProjectsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner"></div></div>
                ) : employeeProjects.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 20px', background: '#f9fafb', borderRadius: '14px' }}>
                    <Briefcase size={36} color="#d1d5db" />
                    <p style={{ color: '#9ca3af', marginTop: '12px', fontSize: '14px' }}>Not assigned to any projects yet.</p>
                    <button className="btn btn-primary" onClick={() => { setSelectedEmployee(null); setActiveTab('projects'); }}
                      style={{ marginTop: '12px', fontSize: '13px' }}>View All Projects</button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {employeeProjects.map(p => {
                      const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.upcoming;
                      const detail = detailLoadedRef.current[p.id];
                      const isEditingClient = editingClientProjectId === p.id;
                      return (
                        <div key={p.id} className="portal-info-card" style={{ padding: '20px 24px' }}>
                          {/* Project header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <strong style={{ fontSize: '15px' }}>{p.title}</strong>
                                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: '#6b7280' }}>
                                {p.event_date && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><Calendar size={12} />{formatDate(p.event_date)}</span>}
                                {p.location && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><MapPin size={12} />{p.location}</span>}
                              </div>
                            </div>
                            <button onClick={() => { setSelectedEmployee(null); setActiveTab('projects'); openProjectDetail(p); }}
                              style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '5px 12px', fontSize: '12px', fontWeight: 600, color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}>
                              Open Project →
                            </button>
                          </div>

                          {/* Client section */}
                          <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.6px', minWidth: '40px' }}>Client</span>
                              {isEditingClient ? (
                                <>
                                  <input
                                    autoFocus
                                    value={editingClientName}
                                    onChange={e => setEditingClientName(e.target.value)}
                                    placeholder="Client / couple name"
                                    style={{ flex: 1, minWidth: '140px', padding: '6px 10px', border: '1.5px solid #be185d', borderRadius: '7px', fontSize: '13px', fontFamily: 'var(--font-body)' }}
                                    onKeyDown={e => { if (e.key === 'Enter') handleUpdateProjectClient(p.id); if (e.key === 'Escape') setEditingClientProjectId(null); }}
                                  />
                                  <button onClick={() => handleUpdateProjectClient(p.id)}
                                    style={{ background: '#be185d', color: 'white', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Save</button>
                                  <button onClick={() => setEditingClientProjectId(null)}
                                    style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '7px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-body)', color: '#6b7280' }}>Cancel</button>
                                </>
                              ) : (
                                <>
                                  <span style={{ fontSize: '14px', color: p.client_name ? '#374151' : '#9ca3af', fontWeight: p.client_name ? 500 : 400 }}>
                                    {p.client_name || 'No client added'}
                                  </span>
                                  <button
                                    onClick={() => { setEditingClientProjectId(p.id); setEditingClientName(p.client_name || ''); }}
                                    style={{ background: 'none', border: 'none', color: '#be185d', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)', padding: '2px 6px', borderRadius: '5px' }}>
                                    {p.client_name ? 'Edit' : '+ Add Client'}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Assigned team members on this project */}
                          {detail?.assignments?.length > 0 && (
                            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '12px', marginTop: '12px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Team on project</span>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                                {detail.assignments.map(a => (
                                  <span key={a.id} style={{ fontSize: '12px', background: a.employee_id === selectedEmployee.id ? '#fce7f3' : '#f3f4f6', color: a.employee_id === selectedEmployee.id ? '#be185d' : '#6b7280', padding: '3px 10px', borderRadius: '20px', fontWeight: a.employee_id === selectedEmployee.id ? 700 : 400 }}>
                                    {a.first_name ? `${a.first_name} ${a.last_name || ''}` : a.invited_email}
                                    {a.role_on_project ? ` · ${a.role_on_project}` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              /* ── Employee List View ── */
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                  <button className="btn btn-primary" onClick={() => setShowInviteForm(v => !v)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Plus size={16} /> {t('vendorTeam.inviteTeamMember')}
                  </button>
                </div>

                {showInviteForm && (
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px' }}>{t('vendorTeam.inviteFormTitle')}</h3>
                    <form onSubmit={handleInvite}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={labelStyle}>Email *</label>
                          <div style={{ position: 'relative' }}>
                            <Mail size={15} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                            <input type="email" required style={{ ...inputStyle, paddingLeft: '34px' }}
                              placeholder="employee@example.com" value={inviteForm.email}
                              onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} />
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>Job Title (optional)</label>
                          <input style={inputStyle} placeholder="e.g. Photographer" value={inviteForm.jobTitle}
                            onChange={e => setInviteForm(p => ({ ...p, jobTitle: e.target.value }))} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="submit" className="btn btn-primary" disabled={inviteSaving}
                          style={{ padding: '9px 20px', fontSize: '13px' }}>
                          {inviteSaving ? t('vendorTeam.sending') : t('vendorTeam.sendInvite')}
                        </button>
                        <button type="button" className="btn btn-outline" onClick={() => { setShowInviteForm(false); setInviteForm(EMPTY_INVITE); }}
                          style={{ padding: '9px 20px', fontSize: '13px' }}>Cancel</button>
                      </div>
                    </form>
                  </div>
                )}

                {employees.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: '12px' }}>
                    <Users size={48} color="#d1d5db" />
                    <h3 style={{ color: '#6b7280', marginTop: '16px' }}>{t('vendorTeam.noTeamYet')}</h3>
                    <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('vendorTeam.noTeamText')}</p>
                  </div>
                ) : (() => {
                  const PHOTO_RE = /photo|photographer|cinematograph/i;
                  const VIDEO_RE = /video|videograph|film|cinema/i;
                  const photoTeam = employees.filter(e => PHOTO_RE.test(e.job_title || ''));
                  const videoTeam = employees.filter(e => VIDEO_RE.test(e.job_title || ''));
                  const hasPhotoOrVideo = photoTeam.length > 0 || videoTeam.length > 0;
                  const visibleEmployees = !hasPhotoOrVideo || teamSubTab === 'all' ? employees
                    : teamSubTab === 'photo' ? photoTeam : videoTeam;
                  return (
                  <>
                    {hasPhotoOrVideo && (
                      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
                        {[
                          { key: 'all', label: `All (${employees.length})` },
                          { key: 'photo', label: `📸 Photo Team (${photoTeam.length})`, show: photoTeam.length > 0 },
                          { key: 'video', label: `🎬 Video Team (${videoTeam.length})`, show: videoTeam.length > 0 },
                        ].filter(t => t.show !== false).map(tab => (
                          <button key={tab.key} onClick={() => setTeamSubTab(tab.key)}
                            style={{ padding: '6px 16px', borderRadius: '20px', border: '1.5px solid', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                              background: teamSubTab === tab.key ? '#be185d' : 'white',
                              color: teamSubTab === tab.key ? 'white' : '#6b7280',
                              borderColor: teamSubTab === tab.key ? '#be185d' : '#e5e7eb',
                            }}>
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {visibleEmployees.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f9fafb', borderRadius: '12px' }}>
                        <Users size={36} color="#d1d5db" />
                        <p style={{ color: '#9ca3af', marginTop: '12px', fontSize: '14px' }}>No team members in this category yet.</p>
                      </div>
                    ) : (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {visibleEmployees.map(emp => (
                      <div key={emp.id} className="portal-info-card" style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => openEmployeeDetail(emp)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                              <strong style={{ fontSize: '14px', color: '#1f2937', textDecoration: 'underline', textDecorationColor: '#fce7f3', textUnderlineOffset: '3px' }}>
                                {emp.first_name ? `${emp.first_name} ${emp.last_name || ''}` : emp.invited_email}
                              </strong>
                              <span style={{
                                fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                background: emp.invite_status === 'accepted' ? '#f0fdf4' : emp.invite_status === 'revoked' ? '#fef2f2' : '#fffbeb',
                                color: emp.invite_status === 'accepted' ? '#22c55e' : emp.invite_status === 'revoked' ? '#ef4444' : '#f59e0b',
                              }}>
                                {emp.invite_status === 'accepted' ? t('vendorTeam.activeStatus') : emp.invite_status === 'revoked' ? t('vendorTeam.revokedStatus') : t('vendorTeam.pendingStatus')}
                              </span>
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              {emp.job_title && <span>{emp.job_title}</span>}
                              {emp.first_name && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><Mail size={11} />{emp.invited_email}</span>}
                              {emp.phone && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><Phone size={11} />{emp.phone}</span>}
                              {emp.location && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><MapPin size={11} />{emp.location}</span>}
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: emp.project_count > 0 ? '#be185d' : '#9ca3af', fontWeight: emp.project_count > 0 ? 600 : 400 }}>
                                <Briefcase size={11} />{emp.project_count || 0} project{emp.project_count !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn btn-outline" onClick={() => openEmployeeProfile(emp)}
                              style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <User size={13} /> Profile
                            </button>
                            {emp.invite_status === 'accepted' && (
                              <button className="btn btn-outline" onClick={() => handleRevokeEmployee(emp.id)}
                                style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: '#f59e0b', borderColor: '#fcd34d' }}>
                                <UserX size={13} /> Revoke
                              </button>
                            )}
                            {emp.invite_status === 'revoked' && (
                              <button className="btn btn-outline" onClick={() => handleReinstateEmployee(emp.id)}
                                style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: '#22c55e', borderColor: '#86efac' }}>
                                <UserCheck size={13} /> Reinstate
                              </button>
                            )}
                            {(emp.invite_status === 'pending' || emp.invite_status === 'pending_signup') && (
                              <button className="btn btn-outline" onClick={async () => {
                                try {
                                  await resendEmployeeInvite(emp.id);
                                  toast.success('Invitation resent');
                                } catch (err) {
                                  toast.error(err.response?.data?.error || 'Failed to resend invite');
                                }
                              }}
                                style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: '#3b82f6', borderColor: '#93c5fd' }}>
                                <Mail size={13} /> Resend
                              </button>
                            )}
                            {(emp.invite_status === 'pending' || emp.invite_status === 'pending_signup') && (
                              <button className="btn btn-outline" onClick={() => handleCancelInvite(emp)}
                                style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: '#b45309', borderColor: '#fcd34d' }}>
                                <X size={13} /> Cancel Invite
                              </button>
                            )}
                            {emp.invite_status !== 'accepted' && (
                              <button className="btn btn-outline" onClick={() => handleDeleteEmployee(emp.id)}
                                style={{ padding: '5px 10px', fontSize: '12px', color: '#ef4444', borderColor: '#fca5a5' }}>
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                    )}
                  </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ===== EMPLOYEE PROFILE MODAL ===== */}
        {profileModal && (
          <div className="modal-backdrop" onClick={() => setProfileModal(null)}>
            <div className="modal-content" style={{ maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h2 className="modal-title" style={{ margin: '0 0 4px' }}>
                    {profileData?.first_name ? `${profileData.first_name} ${profileData.last_name || ''}` : profileData?.invited_email || 'Team Member'}
                  </h2>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {profileData?.job_title && (
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>{profileData.job_title}</span>
                    )}
                    <span style={{
                      fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                      background: profileData?.invite_status === 'accepted' ? '#f0fdf4' : profileData?.invite_status === 'revoked' ? '#fef2f2' : '#fffbeb',
                      color: profileData?.invite_status === 'accepted' ? '#22c55e' : profileData?.invite_status === 'revoked' ? '#ef4444' : '#f59e0b',
                    }}>
                      {profileData?.invite_status === 'accepted' ? 'Active' : profileData?.invite_status === 'revoked' ? 'Revoked' : 'Pending'}
                    </span>
                    {profileData?.project_count > 0 && (
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>{profileData.project_count} project{profileData.project_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => setProfileModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                  <X size={20} />
                </button>
              </div>

              {profileLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner"></div></div>
              ) : (
                <div style={{ display: 'grid', gap: '14px' }}>
                  {/* Name row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>First Name</label>
                      <div style={{ position: 'relative' }}>
                        <User size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                        <input style={{ ...inputStyle, paddingLeft: '32px' }} placeholder="First name"
                          value={profileForm.firstName || ''} onChange={e => setProfileForm(p => ({ ...p, firstName: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Last Name</label>
                      <input style={inputStyle} placeholder="Last name"
                        value={profileForm.lastName || ''} onChange={e => setProfileForm(p => ({ ...p, lastName: e.target.value }))} />
                    </div>
                  </div>

                  {/* Email + Phone */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>Email</label>
                      <div style={{ position: 'relative' }}>
                        <Mail size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                        <input style={{ ...inputStyle, paddingLeft: '32px', background: '#f9fafb', color: '#6b7280' }}
                          value={profileData?.email || profileData?.invited_email || ''} readOnly />
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Phone Number</label>
                      <div style={{ position: 'relative' }}>
                        <Phone size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                        <input style={{ ...inputStyle, paddingLeft: '32px' }} placeholder="+977-98XXXXXXXX"
                          value={profileForm.phone || ''} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  {/* Job title + DOB */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>Job Title</label>
                      <input style={inputStyle} placeholder="e.g. Lead Photographer"
                        value={profileForm.jobTitle || ''} onChange={e => setProfileForm(p => ({ ...p, jobTitle: e.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle}>Date of Birth</label>
                      <input type="date" style={inputStyle}
                        value={profileForm.dateOfBirth || ''} onChange={e => setProfileForm(p => ({ ...p, dateOfBirth: e.target.value }))} />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label style={labelStyle}>Address</label>
                    <div style={{ position: 'relative' }}>
                      <MapPin size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                      <input style={{ ...inputStyle, paddingLeft: '32px' }} placeholder="Street, City, District"
                        value={profileForm.address || ''} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))} />
                    </div>
                  </div>

                  {/* YouTube */}
                  <div>
                    <label style={labelStyle}>YouTube Profile Link</label>
                    <div style={{ position: 'relative' }}>
                      <Youtube size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#ef4444' }} />
                      <input style={{ ...inputStyle, paddingLeft: '32px' }} placeholder="https://youtube.com/@username"
                        value={profileForm.youtubeUrl || ''} onChange={e => setProfileForm(p => ({ ...p, youtubeUrl: e.target.value }))} />
                    </div>
                    {profileForm.youtubeUrl && (
                      <a href={profileForm.youtubeUrl} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#be185d', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <ExternalLink size={11} /> Open channel
                      </a>
                    )}
                  </div>

                  {/* Bio */}
                  <div>
                    <label style={labelStyle}>Bio / About</label>
                    <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
                      placeholder="Short professional background..."
                      value={profileForm.bio || ''} onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))} />
                  </div>

                  {/* Emergency Contact */}
                  <div>
                    <label style={labelStyle}>Emergency Contact</label>
                    <input style={inputStyle} placeholder="Name + phone number"
                      value={profileForm.emergencyContact || ''} onChange={e => setProfileForm(p => ({ ...p, emergencyContact: e.target.value }))} />
                  </div>

                  {/* Type + Location */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>Type</label>
                      <select style={inputStyle} value={profileForm.employeeType || ''}
                        onChange={e => setProfileForm(p => ({ ...p, employeeType: e.target.value }))}>
                        <option value="">-- Select --</option>
                        <option value="Photographer">Photographer</option>
                        <option value="Videographer">Videographer</option>
                        <option value="Photo & Video">Photo &amp; Video</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Location</label>
                      <div style={{ position: 'relative' }}>
                        <MapPin size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                        <input style={{ ...inputStyle, paddingLeft: '32px' }} placeholder="City, District"
                          value={profileForm.location || ''} onChange={e => setProfileForm(p => ({ ...p, location: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  {/* Portfolio Link */}
                  <div>
                    <label style={labelStyle}>Portfolio Link</label>
                    <div style={{ position: 'relative' }}>
                      <ExternalLink size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                      <input style={{ ...inputStyle, paddingLeft: '32px' }} placeholder="https://yourportfolio.com"
                        value={profileForm.portfolioUrl || ''} onChange={e => setProfileForm(p => ({ ...p, portfolioUrl: e.target.value }))} />
                    </div>
                    {profileForm.portfolioUrl && (
                      <a href={profileForm.portfolioUrl} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#be185d', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <ExternalLink size={11} /> Open portfolio
                      </a>
                    )}
                  </div>

                  <div className="modal-actions" style={{ marginTop: '6px' }}>
                    <button className="btn btn-outline" onClick={() => setProfileModal(null)}>Cancel</button>
                    <button className="btn btn-primary" onClick={saveEmployeeProfile} disabled={profileSaving}>
                      {profileSaving ? 'Saving...' : 'Save Profile'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== PROJECT CREATE/EDIT MODAL ===== */}
        {showProjectModal && (
          <div className="modal-backdrop" onClick={() => setShowProjectModal(false)}>
            <div className="modal-content" style={{ maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 className="modal-title" style={{ margin: 0 }}>{editingProject ? t('vendorTeam.editProjectTitle') : t('vendorTeam.newProjectTitle')}</h2>
                <button onClick={() => setShowProjectModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                  <X size={20} />
                </button>
              </div>
              {/* Completed lock banner */}
              {projectForm.status === 'completed' && (
                <div style={{ background: '#fef9c3', border: '1px solid #fcd34d', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#92400e' }}>
                  <span>🔒</span>
                  <span>This project is <strong>completed</strong>. Change the status to make edits.</span>
                </div>
              )}
              <div style={{ display: 'grid', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Project Title *</label>
                  <input style={{ ...inputStyle, opacity: isLocked ? 0.6 : 1 }} placeholder="e.g. Smith Wedding Photography" value={projectForm.title}
                    disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, title: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Client Name</label>
                    <input style={{ ...inputStyle, opacity: isLocked ? 0.6 : 1 }} placeholder="e.g. Smith Family" value={projectForm.clientName}
                      disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, clientName: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>Status <span style={{ color: '#be185d', fontSize: '11px', fontWeight: 400 }}>{isLocked ? '← change to unlock' : ''}</span></label>
                    <select style={inputStyle} value={projectForm.status} onChange={e => setProjectForm(p => ({ ...p, status: e.target.value }))}>
                      <option value="upcoming">Upcoming</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Client Phone</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                      <input style={{ ...inputStyle, paddingLeft: '30px', opacity: isLocked ? 0.6 : 1 }} placeholder="e.g. +977-9800000000" value={projectForm.clientPhone}
                        disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, clientPhone: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Client Email</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                      <input style={{ ...inputStyle, paddingLeft: '30px', opacity: isLocked ? 0.6 : 1 }} placeholder="e.g. client@email.com" value={projectForm.clientEmail}
                        disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, clientEmail: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Event Date</label>
                    <input type="date" style={{ ...inputStyle, opacity: isLocked ? 0.6 : 1 }} value={projectForm.eventDate}
                      disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, eventDate: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>Location / Venue</label>
                    <div style={{ position: 'relative' }}>
                      <MapPin size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                      <input style={{ ...inputStyle, paddingLeft: '30px', opacity: isLocked ? 0.6 : 1 }} placeholder="e.g. Kathmandu, Banquet Hall" value={projectForm.location}
                        disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, location: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Google Maps Link <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                  <div style={{ position: 'relative' }}>
                    <ExternalLink size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
                    <input style={{ ...inputStyle, paddingLeft: '30px', opacity: isLocked ? 0.6 : 1 }} placeholder="https://maps.google.com/..." value={projectForm.mapUrl}
                      disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, mapUrl: e.target.value }))} />
                  </div>
                  {projectForm.mapUrl && (
                    <a href={projectForm.mapUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '12px', color: '#be185d', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                      <ExternalLink size={11} /> Preview on Maps
                    </a>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical', opacity: isLocked ? 0.6 : 1 }} placeholder="Brief description of the project..." value={projectForm.description}
                    disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Internal Notes</label>
                  <textarea style={{ ...inputStyle, height: '60px', resize: 'vertical', opacity: isLocked ? 0.6 : 1 }} placeholder="Notes for your team..." value={projectForm.notes}
                    disabled={isLocked} onChange={e => setProjectForm(p => ({ ...p, notes: e.target.value }))} />
                </div>

                {/* Auto-reminder setting */}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>
                        <Bell size={13} style={{ verticalAlign: 'middle', marginRight: '5px', color: '#be185d' }} />
                        Auto Reminder
                      </label>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af' }}>
                        {projectForm.status === 'completed'
                          ? 'Reminders are disabled for completed projects'
                          : 'Automatically email assigned team members before the event date'}
                      </p>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: '42px', height: '24px', flexShrink: 0, opacity: projectForm.status === 'completed' ? 0.4 : 1 }}>
                      <input type="checkbox" checked={!!projectForm.autoReminderEnabled}
                        disabled={projectForm.status === 'completed'}
                        onChange={e => setProjectForm(p => ({ ...p, autoReminderEnabled: e.target.checked }))}
                        style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{
                        position: 'absolute', cursor: projectForm.status === 'completed' ? 'not-allowed' : 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                        background: projectForm.autoReminderEnabled && projectForm.status !== 'completed' ? '#be185d' : '#d1d5db',
                        borderRadius: '24px', transition: '0.2s',
                      }}>
                        <span style={{
                          position: 'absolute', height: '18px', width: '18px', left: projectForm.autoReminderEnabled && projectForm.status !== 'completed' ? '21px' : '3px',
                          bottom: '3px', background: '#fff', borderRadius: '50%', transition: '0.2s',
                        }} />
                      </span>
                    </label>
                  </div>
                  {projectForm.autoReminderEnabled && projectForm.status !== 'completed' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="number" min={1} max={90} style={{ ...inputStyle, width: '80px' }}
                        value={projectForm.autoReminderDays}
                        onChange={e => setProjectForm(p => ({ ...p, autoReminderDays: parseInt(e.target.value) || 7 }))} />
                      <span style={{ fontSize: '13px', color: '#374151' }}>days before the event date</span>
                    </div>
                  )}
                </div>

                {/* Package Details */}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '14px' }}>
                  <label style={{ ...labelStyle, fontWeight: 700, marginBottom: '10px', display: 'block' }}>Package Inclusions</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                    {[
                      ['audioRecording', 'Audio Recording'],
                      ['droneCoverage', 'Drone Coverage'],
                      ['fullCeremony', 'Full Ceremony Video'],
                      ['highlightsVideo', 'Highlights Video'],
                    ].map(([key, label]) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isLocked ? 'not-allowed' : 'pointer', fontSize: '13px', color: '#374151', opacity: isLocked ? 0.6 : 1 }}>
                        <input type="checkbox"
                          checked={!!(projectForm.packageDetails?.[key])}
                          disabled={isLocked}
                          onChange={e => setProjectForm(p => ({ ...p, packageDetails: { ...(p.packageDetails || {}), [key]: e.target.checked } }))} />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={labelStyle}>Additional Components</label>
                    <input style={{ ...inputStyle, opacity: isLocked ? 0.6 : 1 }} placeholder="e.g. Same-day edit, wedding album"
                      disabled={isLocked}
                      value={projectForm.packageDetails?.additionalComponents || ''}
                      onChange={e => setProjectForm(p => ({ ...p, packageDetails: { ...(p.packageDetails || {}), additionalComponents: e.target.value } }))} />
                  </div>
                </div>

                {/* Delivery Status */}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '14px' }}>
                  <label style={{ ...labelStyle, fontWeight: 700, marginBottom: '10px', display: 'block' }}>Delivery Status</label>
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {/* Photo */}
                    <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px 16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
                        <input type="checkbox" checked={!!projectForm.photoDelivered}
                          onChange={e => setProjectForm(p => ({ ...p, photoDelivered: e.target.checked }))} />
                        📷 Photo Delivered
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '10px', alignItems: 'end' }}>
                        <div>
                          <label style={labelStyle}>Hours {projectForm.photoDelivered && <span style={{ color: '#ef4444' }}>*</span>}</label>
                          <input type="number" min={0} max={24} style={{ ...inputStyle, borderColor: projectForm.photoDelivered && !projectForm.packageDetails?.photoHours ? '#ef4444' : '#d1d5db' }}
                            placeholder="e.g. 10"
                            value={projectForm.packageDetails?.photoHours || ''}
                            onChange={e => setProjectForm(p => ({ ...p, packageDetails: { ...(p.packageDetails || {}), photoHours: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Delivery Link {projectForm.photoDelivered && <span style={{ color: '#ef4444' }}>*</span>}</label>
                          <input style={{ ...inputStyle, borderColor: projectForm.photoDelivered && !projectForm.photoDeliveryLink?.trim() ? '#ef4444' : '#d1d5db' }}
                            placeholder="Google Drive, Dropbox link…"
                            value={projectForm.photoDeliveryLink || ''}
                            onChange={e => setProjectForm(p => ({ ...p, photoDeliveryLink: e.target.value }))} />
                        </div>
                      </div>
                      {projectForm.photoDelivered && !projectForm.photoDeliveryLink?.trim() && (
                        <p style={{ fontSize: '11px', color: '#ef4444', margin: '5px 0 0' }}>Delivery link is required when photos are marked delivered.</p>
                      )}
                    </div>
                    {/* Video */}
                    <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px 16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
                        <input type="checkbox" checked={!!projectForm.videoDelivered}
                          onChange={e => setProjectForm(p => ({ ...p, videoDelivered: e.target.checked }))} />
                        🎥 Video Delivered
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '10px', alignItems: 'end' }}>
                        <div>
                          <label style={labelStyle}>Hours {projectForm.videoDelivered && <span style={{ color: '#ef4444' }}>*</span>}</label>
                          <input type="number" min={0} max={24} style={{ ...inputStyle, borderColor: projectForm.videoDelivered && !projectForm.packageDetails?.videoHours ? '#ef4444' : '#d1d5db' }}
                            placeholder="e.g. 8"
                            value={projectForm.packageDetails?.videoHours || ''}
                            onChange={e => setProjectForm(p => ({ ...p, packageDetails: { ...(p.packageDetails || {}), videoHours: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Delivery Link {projectForm.videoDelivered && <span style={{ color: '#ef4444' }}>*</span>}</label>
                          <input style={{ ...inputStyle, borderColor: projectForm.videoDelivered && !projectForm.videoDeliveryLink?.trim() ? '#ef4444' : '#d1d5db' }}
                            placeholder="YouTube, Google Drive link…"
                            value={projectForm.videoDeliveryLink || ''}
                            onChange={e => setProjectForm(p => ({ ...p, videoDeliveryLink: e.target.value }))} />
                        </div>
                      </div>
                      {projectForm.videoDelivered && !projectForm.videoDeliveryLink?.trim() && (
                        <p style={{ fontSize: '11px', color: '#ef4444', margin: '5px 0 0' }}>Delivery link is required when videos are marked delivered.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Raw Upload Links (for team members) ── */}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    📂 Raw Upload Links <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>(visible only to assigned team members)</span>
                  </h3>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>📷 Raw Photo Upload Link</label>
                      <input style={inputStyle} placeholder="Google Drive, Dropbox or WeTransfer link for raw photos…"
                        value={projectForm.rawPhotoLink || ''}
                        onChange={e => setProjectForm(p => ({ ...p, rawPhotoLink: e.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle}>🎥 Raw Video Upload Link</label>
                      <input style={inputStyle} placeholder="Google Drive, Dropbox or WeTransfer link for raw videos…"
                        value={projectForm.rawVideoLink || ''}
                        onChange={e => setProjectForm(p => ({ ...p, rawVideoLink: e.target.value }))} />
                    </div>
                  </div>
                </div>

                {/* Inline team assignment — only when editing */}
                {editingProject && (
                  <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700 }}>Assigned Team Members</h3>
                    {!modalProjectDetail ? (
                      <p style={{ color: '#9ca3af', fontSize: '13px' }}>Loading…</p>
                    ) : (
                      <>
                        {(modalProjectDetail.assignments || []).length === 0 ? (
                          <p style={{ color: '#9ca3af', fontSize: '13px' }}>{t('vendorTeam.noMembersAssigned')}</p>
                        ) : (
                          <div style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
                            {modalProjectDetail.assignments.map(a => {
                              const isAssigning = assigningTo === a.employee_id;
                              return (
                                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9fafb', borderRadius: '8px', padding: '8px 12px' }}>
                                  <div>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{a.first_name ? `${a.first_name} ${a.last_name || ''}` : a.invited_email}</div>
                                    <div style={{ color: '#6b7280', fontSize: '11px' }}>{a.role_on_project || a.job_title || 'Team member'}</div>
                                  </div>
                                  <button onClick={async () => {
                                    try { await unassignEmployee(editingProject.id, a.employee_id);
                                      const res = await getVendorProject(editingProject.id);
                                      setModalProjectDetail(res.data); detailLoadedRef.current[editingProject.id] = res.data;
                                      toast.success('Unassigned');
                                    } catch { toast.error('Failed'); }
                                  }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px' }}>
                                    <X size={14} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Add assignment */}
                        {acceptedEmployees.filter(e => !(modalProjectDetail.assignments || []).map(a => a.employee_id).includes(e.id)).length > 0 && (
                          assigningTo !== null ? (
                            <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', marginTop: '8px' }}>
                              <select style={{ ...inputStyle, marginBottom: '8px' }} value={assigningTo === 'open' ? '' : assigningTo}
                                onChange={e => setAssigningTo(Number(e.target.value))}>
                                <option value="">-- Select employee --</option>
                                {acceptedEmployees.filter(e => !(modalProjectDetail.assignments || []).map(a => a.employee_id).includes(e.id)).map(e => (
                                  <option key={e.id} value={e.id}>{e.first_name ? `${e.first_name} ${e.last_name || ''}` : e.invited_email}</option>
                                ))}
                              </select>
                              <input style={{ ...inputStyle, marginBottom: '8px' }} placeholder="Role (optional)" value={assignRole} onChange={e => setAssignRole(e.target.value)} />
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button className="btn btn-primary" disabled={!assigningTo || assigningTo === 'open'}
                                  style={{ padding: '6px 14px', fontSize: '12px' }}
                                  onClick={async () => {
                                    try { await assignEmployee(editingProject.id, { employeeId: assigningTo, roleOnProject: assignRole });
                                      const res = await getVendorProject(editingProject.id);
                                      setModalProjectDetail(res.data); detailLoadedRef.current[editingProject.id] = res.data;
                                      setAssigningTo(null); setAssignRole(''); toast.success('Assigned');
                                    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                                  }}>Assign</button>
                                <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => { setAssigningTo(null); setAssignRole(''); }}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button className="btn btn-outline" onClick={() => setAssigningTo('open')}
                              style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                              <Plus size={13} /> Assign Member
                            </button>
                          )
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-actions" style={{ marginTop: '20px' }}>
                <button className="btn btn-outline" onClick={() => setShowProjectModal(false)}>{t('vendorTeam.cancel')}</button>
                <button className="btn btn-primary" onClick={saveProject} disabled={projectSaving}>
                  {projectSaving ? t('vendorTeam.saving') : editingProject ? t('vendorTeam.saveChanges') : t('vendorTeam.createProject')}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ===== CONFIRM DELETE MODAL ===== */}
        {confirmModal && (
          <div className="modal-backdrop" onClick={() => setConfirmModal(null)}>
            <div className="modal-content" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
              <h2 className="modal-title" style={{ margin: '0 0 12px', fontSize: '18px' }}>Are you sure?</h2>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 20px' }}>{confirmModal?.message || 'This action cannot be undone.'}</p>
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setConfirmModal(null)}>Cancel</button>
                <button className="btn btn-primary"
                  style={{ background: '#ef4444', borderColor: '#ef4444' }}
                  onClick={() => { const fn = confirmModal.onConfirm; setConfirmModal(null); fn(); }}>
                  {confirmModal.confirmLabel || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ===== QUICK DELIVERY MODAL ===== */}
        {deliveryModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>Update Delivery Status</h3>
                <button onClick={() => setDeliveryModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}><X size={20} /></button>
              </div>
              <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6b7280' }}>{deliveryModal.project.title}</p>

              {/* Photo section */}
              <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', marginBottom: deliveryModal.photoDelivered ? '12px' : 0 }}>
                  <input type="checkbox" checked={deliveryModal.photoDelivered}
                    onChange={e => setDeliveryModal(d => ({ ...d, photoDelivered: e.target.checked }))} />
                  📷 Photos Delivered
                </label>
                {deliveryModal.photoDelivered && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <input style={inputStyle} placeholder="Photo delivery link (Google Drive, etc.)"
                      value={deliveryModal.photoDeliveryLink}
                      onChange={e => setDeliveryModal(d => ({ ...d, photoDeliveryLink: e.target.value }))} />
                    <input style={inputStyle} type="number" min="0" placeholder="Photo hours (e.g. 8)"
                      value={deliveryModal.photoHours}
                      onChange={e => setDeliveryModal(d => ({ ...d, photoHours: e.target.value }))} />
                  </div>
                )}
              </div>

              {/* Video section */}
              <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', marginBottom: deliveryModal.videoDelivered ? '12px' : 0 }}>
                  <input type="checkbox" checked={deliveryModal.videoDelivered}
                    onChange={e => setDeliveryModal(d => ({ ...d, videoDelivered: e.target.checked }))} />
                  🎥 Videos Delivered
                </label>
                {deliveryModal.videoDelivered && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <input style={inputStyle} placeholder="Video delivery link (YouTube, Google Drive, etc.)"
                      value={deliveryModal.videoDeliveryLink}
                      onChange={e => setDeliveryModal(d => ({ ...d, videoDeliveryLink: e.target.value }))} />
                    <input style={inputStyle} type="number" min="0" placeholder="Video hours (e.g. 6)"
                      value={deliveryModal.videoHours}
                      onChange={e => setDeliveryModal(d => ({ ...d, videoHours: e.target.value }))} />
                  </div>
                )}
              </div>

              {/* Raw upload links for team */}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '14px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  📂 Raw Upload Links <span style={{ fontWeight: 400, color: '#9ca3af', textTransform: 'none', letterSpacing: 0 }}>(visible only to team members)</span>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <input style={inputStyle} placeholder="📷 Raw photo upload link (Drive, WeTransfer…)"
                    value={deliveryModal.rawPhotoLink || ''}
                    onChange={e => setDeliveryModal(d => ({ ...d, rawPhotoLink: e.target.value }))} />
                  <input style={inputStyle} placeholder="🎥 Raw video upload link (Drive, WeTransfer…)"
                    value={deliveryModal.rawVideoLink || ''}
                    onChange={e => setDeliveryModal(d => ({ ...d, rawVideoLink: e.target.value }))} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn btn-outline" onClick={() => setDeliveryModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveDelivery} disabled={deliveryModal.saving}>
                  {deliveryModal.saving ? 'Saving...' : 'Save Delivery Status'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* REASSIGN — swap one member for another */}
        {reassignTarget && (() => {
          const { project, assignment } = reassignTarget;
          const assignedIdSet = new Set((projectDetail?.assignments || []).map(a => a.employee_id));
          const options = assignableEmployees.filter(e => !assignedIdSet.has(e.id));
          const outgoing = assignment.first_name
            ? `${assignment.first_name} ${assignment.last_name || ''}`.trim()
            : assignment.invited_email;
          return (
            <div className="admin-modal-overlay" onClick={() => !reassigning && setReassignTarget(null)}>
              <div className="admin-modal assign-project-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2><UserCheck size={20} /> Reassign</h2>
                  <button className="modal-close" onClick={() => !reassigning && setReassignTarget(null)}><X size={20} /></button>
                </div>
                <div className="assign-project-body">
                  <p className="assign-project-name">{project.title}</p>
                  <p className="assign-project-hint">
                    <strong>{outgoing}</strong>{assignment.role_on_project ? ` (${assignment.role_on_project})` : ''} comes off this project
                    and is emailed to let them know. Whoever takes over is briefed, and the client gets an updated team list.
                  </p>

                  {options.length === 0 ? (
                    <div className="assign-project-empty">
                      There is nobody else on your team to hand this to. Invite someone from the Team tab first.
                    </div>
                  ) : (
                    <>
                      <label className="assign-project-label">Hand over to</label>
                      <select
                        className="assign-project-input"
                        value={reassignToId}
                        onChange={(e) => { setReassignToId(e.target.value); setReassignError(''); }}
                      >
                        <option value="">— Select a team member —</option>
                        {options.map(e => (
                          <option key={e.id} value={e.id}>
                            {e.first_name ? `${e.first_name} ${e.last_name || ''}`.trim() : e.invited_email}
                            {e.job_title ? ` (${e.job_title})` : ''}
                            {e.invite_status !== 'accepted' ? ' — invite pending' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="assign-project-note">
                        They keep the same role{assignment.role_on_project ? ` (${assignment.role_on_project})` : ''} unless you change it afterwards.
                      </p>
                    </>
                  )}

                  {reassignError && <div className="assign-project-error">{reassignError}</div>}

                  <div className="assign-project-actions">
                    <button className="btn btn-outline" onClick={() => setReassignTarget(null)} disabled={reassigning}>Close</button>
                    {options.length > 0 && (
                      <button className="btn btn-primary" onClick={handleReassign} disabled={reassigning || !reassignToId}>
                        {reassigning ? 'Reassigning…' : 'Reassign & Notify'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ASSIGN TEAM MEMBER — from the project list */}
        {assignTarget && (() => {
          const already = new Set((assignTarget.assignees || []).map(a => a.id));
          const options = assignableEmployees.filter(e => !already.has(e.id));
          return (
            <div className="admin-modal-overlay" onClick={() => !assigningFromCard && setAssignTarget(null)}>
              <div className="admin-modal assign-project-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2><UserCheck size={20} /> Assign Team Member</h2>
                  <button className="modal-close" onClick={() => !assigningFromCard && setAssignTarget(null)}><X size={20} /></button>
                </div>
                <div className="assign-project-body">
                  <p className="assign-project-name">{assignTarget.title}</p>
                  <p className="assign-project-hint">
                    The team member is emailed straight away, and
                    {assignTarget.client_email ? ` ${assignTarget.client_email}` : ' the client'} gets a note listing who is covering their wedding.
                  </p>

                  {assignableEmployees.length === 0 ? (
                    <div className="assign-project-empty">
                      You have not added any team members yet. Invite someone from the Team tab first.
                    </div>
                  ) : options.length === 0 ? (
                    <div className="assign-project-empty">
                      Everyone on your team is already assigned to this project.
                    </div>
                  ) : (
                    <>
                      <label className="assign-project-label">Team member</label>
                      <select
                        className="assign-project-input"
                        value={assignEmployeeId}
                        onChange={(e) => { setAssignEmployeeId(e.target.value); setAssignError(''); }}
                      >
                        <option value="">— Select a team member —</option>
                        {options.map(e => (
                          <option key={e.id} value={e.id}>
                            {e.first_name ? `${e.first_name} ${e.last_name || ''}`.trim() : e.invited_email}
                            {e.job_title ? ` (${e.job_title})` : ''}
                            {e.invite_status !== 'accepted' ? ' — invite pending' : ''}
                          </option>
                        ))}
                      </select>

                      <label className="assign-project-label">Role on this project (optional)</label>
                      <input
                        className="assign-project-input"
                        placeholder="e.g. Photography, Video, Makeup"
                        value={assignRoleInput}
                        onChange={(e) => setAssignRoleInput(e.target.value)}
                      />
                      <p className="assign-project-note">
                        This is what the client sees, e.g. “Photography: Karen — 9801234567”.
                        {options.some(e => e.invite_status !== 'accepted') && ' A member who has not accepted their invite yet is still notified at their invited email, and shows to the client as “Confirming”.'}
                      </p>
                    </>
                  )}

                  {assignError && <div className="assign-project-error">{assignError}</div>}

                  <div className="assign-project-actions">
                    <button className="btn btn-outline" onClick={() => setAssignTarget(null)} disabled={assigningFromCard}>Close</button>
                    {options.length > 0 && (
                      <button className="btn btn-primary" onClick={handleAssignFromCard} disabled={assigningFromCard || !assignEmployeeId}>
                        {assigningFromCard ? 'Assigning…' : 'Assign & Notify'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* CANCEL PROJECT — reason is mandatory */}
        {cancelTarget && (
          <div className="admin-modal-overlay" onClick={() => !cancelling && setCancelTarget(null)}>
            <div className="admin-modal cancel-project-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2><AlertCircle size={20} /> Cancel Project</h2>
                <button className="modal-close" onClick={() => !cancelling && setCancelTarget(null)}><X size={20} /></button>
              </div>
              <div className="cancel-project-body">
                <p className="cancel-project-name">{cancelTarget.title}</p>
                <p className="cancel-project-hint">
                  {cancelTarget.client_name ? `${cancelTarget.client_name} · ` : ''}
                  This keeps the project on record and marks it cancelled. Tell us why — at least 10 characters.
                </p>
                <textarea
                  className="cancel-project-input"
                  rows={4}
                  autoFocus
                  placeholder="e.g. Client postponed the wedding to next year"
                  value={cancelReason}
                  onChange={(e) => { setCancelReason(e.target.value); setCancelError(''); }}
                />
                <div className="cancel-project-meta">
                  <span className={cancelReason.trim().length < 10 ? 'short' : 'ok'}>
                    {cancelReason.trim().length}/10 characters minimum
                  </span>
                  {cancelError && <span className="cancel-project-error">{cancelError}</span>}
                </div>
                <div className="cancel-project-actions">
                  <button className="btn btn-outline" onClick={() => setCancelTarget(null)} disabled={cancelling}>
                    Keep Project
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCancelProject}
                    disabled={cancelling || cancelReason.trim().length < 10}
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel Project'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default VendorProjects;
